import {
  AnalyticsInterval,
  AnalyticsQueryContext,
  AnalyticsResponse,
  AnalyticsStats,
  ComparePeriod,
  DynamicMetricsInfo,
} from '@services/analytics-services/core/models';
import { DateRangePresetType } from '@services/core-services/models';
import { DateTime } from 'luxon';

/**
 * Formats date to ISO string.
 * @param date
 */
export const toISOString = (date: Date): string => {
  return DateTime.fromJSDate(date).toISO() || '';
};

export const localIso = (date: Date): string => {
  // Format to UTC without timezone offset information
  return DateTime.fromJSDate(date).toUTC().toFormat("yyyy-MM-dd'T'HH:mm:ss");
};
/**
 * Gets the sum of the stats
 * @param data
 * @param key
 */
export const getStatsSum = <T>(data: AnalyticsStats<T>[], key: keyof T): number => {
  return data.reduce((accumulator, item) => {
    return accumulator + ((item.metrics[key] as number) || 0);
  }, 0);
};

/**
 * Calculates percentage change between stats and compare stats.
 * @param stats
 * @param compare
 * @param key
 */
export const getChangePercentage = <T>(
  stats: AnalyticsStats<T>[],
  compare: AnalyticsStats<T>[],
  key: keyof T
): number => {
  // Determine the length to compare based on the shorter array
  const minLength = Math.min(stats.length, compare.length);

  // If either array is empty (or effectively has 0 length for comparison), return 0%
  if (minLength === 0) {
    return 0;
  }

  // Slice arrays to the same length for fair comparison
  const truncatedStats = stats.slice(0, minLength);
  const truncatedCompare = compare.slice(0, minLength);

  // Calculate sums for the truncated arrays
  const statValue = getStatsSum(truncatedStats, key);
  const compareValue = getStatsSum(truncatedCompare, key);

  // Calculate percentage change based on sums over the matched duration
  if (compareValue === 0) {
    // If comparison sum (over matched duration) is 0, change is 0% if stat sum is also 0.
    // Otherwise, the change is technically infinite. Returning 0 as a default.
    return statValue === 0 ? 0 : 0; // Consider if different handling is needed for non-zero statValue
  }

  // Use Math.abs in the denominator for correct percentage change calculation
  const percent = 100 * ((statValue - compareValue) / Math.abs(compareValue));

  // Round to one decimal place
  return Math.round(percent * 10) / 10;
};

/**
 * Formats the date based on analytics interval
 * @param d
 * @param interval
 * @param greaterThanYear
 */
export const formatAnalyticsDate = (
  d: string | Date,
  interval: AnalyticsInterval = AnalyticsInterval.Month,
  greaterThanYear: boolean = false
) => {
  const date = DateTime.fromJSDate(new Date(d));

  if (interval === AnalyticsInterval.Hour) {
    return date.toFormat('h a');
  }

  return greaterThanYear ? date.toFormat('MMM d, `yy') : date.toFormat('MMM d');
};

/**
 * Gets the default compare period for given range.
 * @param preset
 */
export const getDefaultComparePeriodForRange = (
  preset: DateRangePresetType | string
): ComparePeriod => {
  switch (preset) {
    case DateRangePresetType.Today:
      return ComparePeriod.Yesterday;
    case DateRangePresetType.Yesterday:
      return ComparePeriod.DayBeforeYesterday;
    case DateRangePresetType.D7:
      return ComparePeriod.Previous7Days;
    case DateRangePresetType.D30:
      return ComparePeriod.Preceding30Days;
    case DateRangePresetType.M3:
      return ComparePeriod.Previous3Months;
    case DateRangePresetType.M6:
      return ComparePeriod.Previous6Months;
    case DateRangePresetType.M12:
      return ComparePeriod.Previous12Months;
    case DateRangePresetType.WTD:
      return ComparePeriod.SamePeriodPreviousWeek;
    case DateRangePresetType.MTD:
      return ComparePeriod.SamePeriodPreviousMonth;
    case DateRangePresetType.QTD:
      return ComparePeriod.SamePeriodPreviousQuarter;
    case DateRangePresetType.YTD:
      return ComparePeriod.SamePeriodPreviousYear;
    default:
      return ComparePeriod.Custom;
  }
};

/**
 * Maps analytics response to stats and compare stats.
 * @param response
 * @param context
 */
export const mapAnalyticsResponse = <T>(
  response: AnalyticsResponse<T>,
  context: AnalyticsQueryContext
) => {
  const items = response.content.stats;
  const lifetime_stats = response.content.lifetime_stats;
  const { range, compare } = context;
  const stats = filterItemsByDateRange(items, range.start, range.end);
  const compare_stats = compare ? filterItemsByDateRange(items, compare.start, compare.end) : [];
  return {
    stats,
    compare_stats,
    lifetime_stats,
    context,
  };
};

/**
 * Maps analytics response to stats and compare stats.
 * @param items
 * @param context
 */
export const mapDynamicAnalyticsResponse = (
  items: DynamicMetricsInfo[],
  context: AnalyticsQueryContext
) => {
  const { range, compare } = context;
  const stats = filterItemsByDateRange(items, range.start, range.end);
  const compare_stats = compare ? filterItemsByDateRange(items, compare.start, compare.end) : [];
  return {
    stats,
    compare_stats,
    context,
  };
};

/**
 * Filters analytics stats by filtering ones that belong to range.
 * @param items
 * @param start
 * @param end
 */
export const filterItemsByDateRange = <T>(items: AnalyticsStats<T>[], start: Date, end: Date) => {
  const startDate = DateTime.fromJSDate(start);
  const endDate = DateTime.fromJSDate(end);

  return items.filter(item => {
    const date = DateTime.fromISO(item.date as string);
    return date >= startDate && date <= endDate;
  });
};

/**
 * Gets period label
 * @param period
 */
export const getComparePeriodLabel = (period: ComparePeriod): string => {
  switch (period) {
    case ComparePeriod.Yesterday:
      return 'vs yesterday';
    case ComparePeriod.DayBeforeYesterday:
      return 'vs day before yesterday';
    case ComparePeriod.Previous7Days:
      return 'vs previous 7 days';
    case ComparePeriod.Preceding30Days:
      return 'vs previous 30 days';
    case ComparePeriod.Previous3Months:
      return 'vs previous 3 months';
    case ComparePeriod.Previous6Months:
      return 'vs previous 6 months';
    case ComparePeriod.Previous12Months:
      return 'vs previous year';
    case ComparePeriod.SamePeriodPreviousWeek:
      return 'vs previous week';
    case ComparePeriod.SamePeriodPreviousMonth:
      return 'vs previous month';
    case ComparePeriod.SamePeriodPreviousQuarter:
      return 'vs previous quarter';
    case ComparePeriod.SamePeriodPreviousYear:
      return 'vs previous year';
    default:
      return 'from previous period';
  }
};
